import * as actionsFiles from '../actions/FilesActions';
import { downloadFileFromURl } from '../actions/selectors';

export const DASHBOARD_GUIDE_TYPES = {
  CUSTOMER: 'customer',
  PROJECT: 'project',
};

const GUIDE_SCOPES = ['dashboard', 'portal'];

const FALLBACK_GUIDE_NAMES = {
  [DASHBOARD_GUIDE_TYPES.CUSTOMER]: 'Customer Dashboard.pdf',
  [DASHBOARD_GUIDE_TYPES.PROJECT]: 'Project Dashboard.pdf',
};

const normalizeLang = (lang) => {
  const value = (lang || 'en').toLowerCase().split('-')[0];
  return value === 'iw' ? 'he' : value;
};

const normalizeText = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const normalizePdfName = (value) => {
  const name = String(value || '').trim();
  if (!name) return '';
  return name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
};

const getGuideName = (guide) => {
  if (typeof guide === 'string') return guide;
  return guide?.name || guide?.fileName || guide?.filename || guide?.file || '';
};

const getGuideLang = (guide, currentLang) => normalizeLang(
  typeof guide === 'object' ? (guide.lang || guide.language || guide.locale) : currentLang
);

const getGuideScope = (guide, scope) => {
  if (typeof guide === 'object' && guide.scope) {
    return guide.scope;
  }
  return scope;
};

const guideMatchesDashboard = (guide, dashboardType) => {
  if (typeof guide === 'object') {
    const declaredType = guide.dashboardType || guide.type || guide.dashboard;
    if (declaredType && normalizeText(declaredType) === normalizeText(dashboardType)) {
      return true;
    }
  }

  const guideName = normalizeText(getGuideName(guide));
  if (dashboardType === DASHBOARD_GUIDE_TYPES.CUSTOMER) {
    return guideName.includes('customer') && guideName.includes('dashboard');
  }
  return guideName.includes('project') && guideName.includes('dashboard');
};

const guideNameMatches = (guide, fileName) => {
  const guideName = normalizePdfName(getGuideName(guide)).toLowerCase();
  const expectedName = normalizePdfName(fileName).toLowerCase();
  return guideName && expectedName && guideName === expectedName;
};

const collectGuides = (userGuides = {}) => {
  return GUIDE_SCOPES.flatMap((scope) => {
    const guides = userGuides[scope] || [];
    return Array.isArray(guides)
      ? guides.map((guide) => ({ guide, scope: getGuideScope(guide, scope) }))
      : [];
  });
};

const resolveDashboardGuide = (userGuides, dashboardType, currentLang, expectedGuideName) => {
  const language = normalizeLang(currentLang);
  const guides = collectGuides(userGuides);
  const languageGuides = guides.filter(({ guide }) => getGuideLang(guide, language) === language);
  const englishGuides = guides.filter(({ guide }) => getGuideLang(guide, language) === 'en');
  const fallbackGuideName = FALLBACK_GUIDE_NAMES[dashboardType];
  const candidates = guides.filter(({ guide }) => guideMatchesDashboard(guide, dashboardType));

  return languageGuides.find(({ guide }) => guideNameMatches(guide, expectedGuideName))
    || englishGuides.find(({ guide }) => guideNameMatches(guide, fallbackGuideName))
    || candidates.find(({ guide }) => getGuideLang(guide, language) === language)
    || candidates.find(({ guide }) => getGuideLang(guide, language) === 'en')
    || candidates[0]
    || {
      guide: {
        name: fallbackGuideName,
        lang: language,
      },
      scope: 'portal',
    };
};

export const downloadDashboardGuide = ({
  dashboardType,
  dispatch,
  projectId,
  currentLang,
  expectedGuideName,
  userGuides,
  onFailure,
}) => {
  if (!dispatch || !projectId) {
    return;
  }

  const resolvedGuide = resolveDashboardGuide(userGuides, dashboardType, currentLang, expectedGuideName);
  const normalizedGuideName = getGuideName(resolvedGuide.guide).endsWith('.pdf')
    ? getGuideName(resolvedGuide.guide)
    : `${getGuideName(resolvedGuide.guide)}.pdf`;
  const guideLanguage = getGuideLang(resolvedGuide.guide, currentLang);
  const filePath = `user-guides/${resolvedGuide.scope}/${guideLanguage}/${normalizedGuideName}`;

  dispatch(
    actionsFiles.getUrlForDownloadFile(projectId, filePath, 'manual', '0', (res) => {
      if (res?.status && res?.data) {
        downloadFileFromURl(res.data, normalizedGuideName);
        return;
      }

      if (onFailure) {
        onFailure(res);
      }
    })
  );
};
